// Prompt supply. Design is LLM-on-the-fly, drawn from a fixed taxonomy so every
// session is fresh and nothing repeats. When the /api/generate-prompt function
// isn't configured yet (no LLM key), we fall back to a starter bank so the app
// is fully usable on day one.

import { SHAPES, REPS_PER_SESSION } from "./config";

// Taxonomy of prompt TYPES the LLM is told to draw from. This is what keeps
// on-the-fly generation from drifting into junk.
export const TAXONOMY = [
  "state an opinion on a debatable claim",
  "explain a concept to a smart non-expert in plain terms",
  "make a decision between two reasonable options and justify it",
  "disagree with a common piece of advice",
  "summarize what matters about a situation",
  "persuade someone to take one specific action",
  "define a fuzzy term precisely",
  "react to a short workplace scenario",
];

// Fallback bank, tagged by the shape they suit. Reviewed, safe, generic.
const STARTER_BANK = {
  per: [
    "Should a small law firm build its own software or buy off-the-shelf tools?",
    "Is it better to specialize in one practice area or stay a generalist?",
    "Do daily standup meetings help teams or waste time?",
    "Should professionals share their rates publicly on their website?",
    "Is a four-day work week a good idea for a solo practice?",
    "Are written contracts always better than a handshake for small deals?",
  ],
  rec: [
    "A client wants a result you think is unrealistic. What do you recommend?",
    "You have one free hour this week to grow your business. How do you spend it?",
    "A colleague asks whether to answer a cold recruiter's message. What's your call?",
    "You're deciding between a cheaper tool that's clunky and a pricier one that's smooth. Which?",
    "Someone asks if they should send a follow-up email or just wait. Advise them.",
    "You can either raise prices 20% or take on two more clients. Which do you pick?",
  ],
  ans: [
    "Someone asks: what's the single most useful habit for clear communication?",
    "A friend asks how to start using AI in their small business. What do you tell them?",
    "Someone asks why you got into your line of work. Answer them.",
    "A new hire asks what one thing they should focus on this month. Answer.",
    "Someone asks how you decide which tasks to do first each day. Answer.",
    "A client asks what makes your service different. Answer.",
  ],
};

function pick(arr, seed) {
  return arr[Math.floor((seed || Math.random()) * arr.length) % arr.length];
}

// Rotate shapes across the reps so a session hits variety: per, rec, ans, per...
function shapeForRep(i) {
  return SHAPES[i % SHAPES.length];
}

function bankPrompts(count) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const shape = shapeForRep(i);
    out.push({ text: pick(STARTER_BANK[shape.id]), shape });
  }
  return out;
}

// Try the LLM function; fall back to the bank on any failure.
export async function getSessionPrompts(count = REPS_PER_SESSION) {
  const shapes = [];
  for (let i = 0; i < count; i++) shapes.push(shapeForRep(i));

  try {
    const res = await fetch("/api/generate-prompt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        count,
        shapes: shapes.map((s) => ({ id: s.id, label: s.label })),
        taxonomy: TAXONOMY,
      }),
    });
    if (!res.ok) throw new Error("generate-prompt " + res.status);
    const data = await res.json();
    if (!Array.isArray(data.prompts) || data.prompts.length !== count)
      throw new Error("bad shape");
    return data.prompts.map((p, i) => ({
      text: String(p.text || p).trim(),
      shape: shapes[i],
    }));
  } catch (e) {
    // No key / offline / error — use the reviewed bank.
    return bankPrompts(count);
  }
}
